import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, PenLine, FileEdit, Sparkles, ArrowRight } from "lucide-react";
import Comments from "@/components/Comments";

export default function Thesis() {
  const [form, setForm] = useState({ field: "", interest: "", jenjang: "S1" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [thesisId, setThesisId] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [picking, setPicking] = useState(null); // index being picked
  const nav = useNavigate();
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => { http.get("/thesis-drafts/mine").then(r => setDrafts(r.data)).catch(()=>{}); }, []);

  const generate = async () => {
    if (!form.field.trim() || !form.interest.trim()) { toast.error("Isi bidang dan minat."); return; }
    setLoading(true); setResult(null);
    try {
      const { data: job } = await http.post("/ai/thesis", form);
      const jobId = job.job_id;
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: j } = await http.get(`/ai/job/${jobId}`);
        if (j.status === "done") {
          setResult(j.result.result); setThesisId(j.result.thesis_id);
          toast.success("5 usulan judul berhasil dibuat");
          return;
        }
        if (j.status === "error") throw new Error(j.error || "AI error");
      }
      throw new Error("Timeout menunggu AI");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "AI gagal menghasilkan ide");
    } finally { setLoading(false); }
  };

  const pickTitle = async (i) => {
    if (!thesisId) return;
    setPicking(i);
    try {
      const { data } = await http.post(`/thesis/${thesisId}/select-title`, { title_index: i });
      toast.success("Draft dibuat. Menuju editor…");
      nav(`/tugas-akhir/draft/${data.draft_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memulai draft");
    } finally { setPicking(null); }
  };

  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[#D97706] font-semibold">Tugas Akhir</p>
      <h1 className="serif text-3xl font-bold text-[#0F2922] mt-1">Ide & Roadmap Tugas Akhir</h1>
      <p className="text-slate-600 mt-2">5 usulan judul + roadmap Bab 1-5 + daftar pustaka APA otomatis. Pilih judul favoritmu untuk melanjutkan penulisan bab per bab.</p>

      {drafts.length > 0 && (
        <div className="mt-6 kt-card p-5" data-testid="thesis-drafts-section">
          <div className="flex items-center gap-2">
            <FileEdit size={16} className="text-[#D97706]"/>
            <h2 className="font-semibold text-[#0F2922]">Draft Tugas Akhir Saya</h2>
            <Badge variant="outline" className="ml-auto">{drafts.length}</Badge>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {drafts.map(d => {
              const total = 7; const done = Object.keys(d.chapters || {}).filter(k => (d.chapters[k] || "").trim()).length;
              return (
                <button key={d.draft_id} onClick={() => nav(`/tugas-akhir/draft/${d.draft_id}`)} className="text-left rounded-xl border border-slate-200 hover:border-[#D97706] hover:shadow-md transition p-4 bg-white" data-testid={`draft-open-${d.draft_id}`}>
                  <Badge className="bg-[#0F2922] text-[#FAFA96] text-[10px]">{d.jenjang} · {d.field || "-"}</Badge>
                  <div className="font-semibold text-[#0F2922] mt-2 text-sm line-clamp-2">{d.selected_title}</div>
                  <div className="mt-3 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#D97706]" style={{ width: `${(done/total)*100}%`}}/></div>
                  <div className="text-xs text-slate-500 mt-2 flex items-center justify-between">
                    <span>{done}/{total} bab</span>
                    <span className="text-[#D97706] font-semibold inline-flex items-center gap-1">Lanjut <ArrowRight size={11}/></span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 kt-card p-6 grid md:grid-cols-2 gap-4">
        <div><Label>Bidang studi</Label>
          <Input value={form.field} onChange={e=>set("field", e.target.value)} placeholder="Contoh: Teknik Informatika" data-testid="thesis-field-input" />
        </div>
        <div><Label>Jenjang</Label>
          <Select value={form.jenjang} onValueChange={v=>set("jenjang", v)}>
            <SelectTrigger data-testid="thesis-jenjang-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="S1">S1</SelectItem>
              <SelectItem value="S2">S2</SelectItem>
              <SelectItem value="S3">S3</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2"><Label>Minat / topik spesifik</Label>
          <Textarea value={form.interest} onChange={e=>set("interest", e.target.value)} placeholder="Contoh: penggunaan AI untuk klasifikasi penyakit tanaman padi" data-testid="thesis-topic-input" />
        </div>
        <div className="md:col-span-2">
          <Button disabled={loading} onClick={generate} className="bg-[#0F2922] hover:bg-[#173E34] text-[#FAFA96]" data-testid="thesis-generate-btn">
            {loading ? <><Loader2 className="mr-2 animate-spin" size={16}/> Menghasilkan…</> : "Generate 5 Judul + Roadmap"}
          </Button>
        </div>
      </div>

      {result?.titles && (
        <div className="mt-8 kt-card p-6">
          <Accordion type="single" collapsible defaultValue="t-0">
            {result.titles.map((t, i) => (
              <AccordionItem key={i} value={`t-${i}`} data-testid={`thesis-title-${i}`}>
                <AccordionTrigger className="text-left">
                  <span className="text-xs text-[#D97706] uppercase tracking-widest mr-3">Judul {i+1}</span>
                  <span className="font-semibold text-[#0F2922]">{t.title}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm mt-1"><b>Novelty:</b> {t.novelty}</p>
                  <div className="mt-3"><b className="text-sm">Rumusan Masalah:</b>
                    <ul className="list-disc pl-5 text-sm text-slate-700">{(t.rumusan_masalah||[]).map((x,j)=><li key={j}>{x}</li>)}</ul>
                  </div>
                  <div className="mt-3"><b className="text-sm">Tujuan:</b>
                    <ul className="list-disc pl-5 text-sm text-slate-700">{(t.tujuan||[]).map((x,j)=><li key={j}>{x}</li>)}</ul>
                  </div>
                  <div className="mt-3 text-sm"><b>Metodologi:</b> {t.metodologi}</div>
                  <div className="mt-3"><b className="text-sm">Tahapan Penyelesaian:</b>
                    <div className="grid md:grid-cols-2 gap-2 mt-2">
                      {(t.tahapan||[]).map((s,j)=>(
                        <div key={j} className="border border-slate-200 rounded-lg p-3 bg-[#FAFA96]/30 text-sm">
                          <div className="font-semibold text-[#0F2922]">{s.bab}</div>
                          <div className="text-slate-700 mt-1">{s.aktivitas}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3"><b className="text-sm">Daftar Pustaka (APA):</b>
                    <ol className="list-decimal pl-5 text-sm text-slate-700 mt-1 space-y-1">
                      {(t.daftar_pustaka||[]).map((r,j)=><li key={j}>{r}</li>)}
                    </ol>
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-200 flex flex-wrap gap-2 items-center">
                    <div className="text-xs text-slate-600 flex-1 min-w-[200px]">
                      <Sparkles size={12} className="inline text-[#D97706] mr-1"/>
                      Puas dengan judul ini? Lanjutkan penulisan bab per bab dan ekspor ke Google Docs (.docx).
                    </div>
                    <Button
                      onClick={() => pickTitle(i)}
                      disabled={picking === i}
                      className="bg-[#D97706] hover:bg-[#B45309] text-white"
                      data-testid={`thesis-pick-title-${i}`}
                    >
                      {picking === i ? <><Loader2 className="mr-2 animate-spin" size={14}/> Membuat draft…</> : <><PenLine size={14} className="mr-2"/> Pilih & Tulis Bab per Bab</>}
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {thesisId && <Comments contentType="thesis" contentId={thesisId} />}
        </div>
      )}
    </div>
  );
}
